import { useEffect } from 'react'

import { productCodeLittleLoans } from '@mediaingenuity/totallymoney.cardloanutils'
import { useRouter } from 'next/router'

import Box from '@totallymoney/ui/components/Box'
import Grid from '@totallymoney/ui/components/Grid'
import Heading from '@totallymoney/ui/components/Heading'
import LinkWithChevron from '@totallymoney/ui/components/LinkWithChevron'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'
import theme from '@totallymoney/ui/theme'

import {
    CreditCardAlternativeProduct,
    CreditCardAlternativeProductsCategory,
} from '@appTypes/EligibilityApi.interface'

import BrokerInfo from '@components/BrokerInfo'

import useAppQueryState from '@hooks/useAppQueryState'
import useEffectOnce from '@hooks/useEffectOnce'
import useLogEvents from '@hooks/useLogEvents'
import useLogEvents2 from '@hooks/useLogEvents2'
import useSetState from '@hooks/useSetState'

import { useMinimumGuaranteedCreditLimitQuery } from '@queries/useMinimumGuaranteedCreditLimitQuery'

import useGetAlternativeProductApplicationUrl from '@routes/AlternativeProductsTableRoute/hooks/useGetAlternativeProductApplicationUrl'
import HighlightHeroIneligible from '@routes/ScoredTableRoute/components/ScoredTable/components/HighlightHero/components/HighlightHeroIneligible'

import {
    buildAcquisitionJourneyAlternativeProductsTableClickoutEventPayload,
    buildAcquisitionJourneyFormDeclineEventPayload,
} from '@utils/buildAcquisitionEvents'
import { getAppConfig } from '@utils/getAppConfig'
import getCardPosition from '@utils/getCardPosition'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'
import { getUuid } from '@utils/getUuid'

import AlternativeProductsTableHeader from './components/AlternativeProductsTableHeader'
import AlternativeProductsTableItem from './components/AlternativeProductsTableItem'
import LittleLoansOfferCard from './components/LittleLoansOfferCard'
import getCategoryByAlternativeCategoryId from './utils/getCategoryByAlternativeProductCategoryId'

interface AlternativeProductsTableProps {
    alternativeProductCategories: CreditCardAlternativeProductsCategory[]
}

interface AlternativeProductsTableState {
    selectedCategory: string
}

const AlternativeProductsTable = ({
    alternativeProductCategories,
}: AlternativeProductsTableProps) => {
    const appConfig = getAppConfig()
    const { appQueryState } = useAppQueryState()
    const { query, isReady } = useRouter()

    const getAlternativeProductApplicationUrl =
        useGetAlternativeProductApplicationUrl()
    const logEvents = useLogEvents()
    const { logAlternativeProductClickEvent, logResultsScreenDisplayedEvent } =
        useLogEvents2()

    const { data } = useMinimumGuaranteedCreditLimitQuery({
        customerId: appQueryState.customerId,
    })

    const isNewSignUpJourney = query.isNewSignUp === 'true'

    const [alternativeProductsState, setAlternativeProductsState] =
        useSetState<AlternativeProductsTableState>({
            selectedCategory: alternativeProductCategories[0]?.id ?? '',
        })

    const activeCategory = getCategoryByAlternativeCategoryId(
        alternativeProductCategories,
        alternativeProductsState.selectedCategory
    )

    useEffect(() => {
        if (activeCategory && activeCategory.alternativeProducts.length > 0) {
            const eventData = buildAcquisitionJourneyFormDeclineEventPayload(
                activeCategory.alternativeProducts,
                isNewSignUpJourney
            )

            if (eventData) {
                logEvents.logAcquisitionJourneyCardsTableViewEvent(eventData)
            }
        }
    }, [isReady])

    useEffectOnce(() => {
        const littleLoansProduct = {
            apr: 79.5,
            name: 'Little Loans',
            productCode: productCodeLittleLoans,
        } as CreditCardAlternativeProduct

        logResultsScreenDisplayedEvent({
            displayedProducts: [
                ...(activeCategory?.alternativeProducts ?? []),
                littleLoansProduct,
            ],
            highlightProduct: null,
            category: 'credit_cards_alt',
            maxCreditLimit: data?.minimumGuaranteedCreditLimit!,
        })
    }, isReady)

    const onCategoryChange = (category: string) => {
        logEvents.logAlternativeProductCategoryChangeEvent({
            categoryName: category,
        })

        setAlternativeProductsState({
            selectedCategory: category,
        })
    }

    const onApplyPress = ({
        alternativeProduct,
        productPosition,
        applicationUrl,
        clickId,
    }: {
        alternativeProduct: CreditCardAlternativeProduct
        applicationUrl: string
        clickId: string
        productPosition: number
    }) => {
        logEvents.logAlternativeProductApplyEvent({
            productCode: alternativeProduct.productCode,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            rowNumber: productPosition,
            trigger: 'row',
        })

        logAlternativeProductClickEvent({
            altCreditCard: alternativeProduct,
            category: 'credit_cards_alt',
            productShownOrder: productPosition,
            productRankOrder: null,
            clickId,
        })

        window.open(applicationUrl, '_blank')
    }

    const showHighlightedCreditCardHero =
        appQueryState.scoredCardUrl != null && appQueryState.isCardSpecific

    return (
        <>
            <Box backgroundColor="productNeutral80">
                <Grid>
                    <Box mt="space30" mb="space10" gridColumn="1/13">
                        <LinkWithChevron
                            href={`${appConfig.ACCOUNT_URL}offers?customerId=${appQueryState.customerId}`}
                            variant="100"
                            direction="left"
                        >
                            Back to offers
                        </LinkWithChevron>
                    </Box>
                </Grid>
                <Grid>
                    <Box gridColumn="1/13" pb="space20">
                        <Stack space="space30">
                            <Stack space="space10">
                                <Heading variant="100">
                                    Your card results
                                </Heading>
                                <Text variant="120">
                                    We couldn’t find any cards you’re eligible
                                    for right now, but we’ve got some other
                                    options for you.
                                </Text>
                            </Stack>

                            {showHighlightedCreditCardHero ? (
                                <HighlightHeroIneligible
                                    item={null}
                                    totalCreditCards={0}
                                />
                            ) : null}
                        </Stack>
                    </Box>
                </Grid>
            </Box>

            <Box backgroundColor="productNeutral80">
                {alternativeProductCategories.length > 0 ? (
                    <Box pt="space20">
                        <AlternativeProductsTableHeader
                            categories={alternativeProductCategories}
                            initialCategory={alternativeProductCategories[0].id}
                            onCategoryChange={onCategoryChange}
                        />

                        <Box pb="space50" />
                        <Grid>
                            <Box gridColumn="1/13" mb="space50">
                                <Box mb="space20">
                                    <Heading variant="60">
                                        {activeCategory?.displayName}
                                    </Heading>
                                </Box>
                                <Text variant="120">
                                    {activeCategory?.description}
                                </Text>
                            </Box>
                        </Grid>
                        <Grid style={{ gridGap: theme.space30 }}>
                            {activeCategory?.alternativeProducts.map(
                                (alternativeProduct, index) => {
                                    const clickId = getUuid()
                                    const applyUrl =
                                        getAlternativeProductApplicationUrl({
                                            productCode:
                                                alternativeProduct.productCode,
                                            clickId,
                                        })

                                    return (
                                        <Box
                                            gridColumn="1/13"
                                            key={alternativeProduct.productCode}
                                        >
                                            <AlternativeProductsTableItem
                                                item={alternativeProduct}
                                                applyHref={applyUrl}
                                                isPortraitImage={getIsProductPortraitImage(
                                                    alternativeProduct.productCode
                                                )}
                                                onApplyPress={() => {
                                                    const acquisitionClickoutEventData =
                                                        buildAcquisitionJourneyAlternativeProductsTableClickoutEventPayload(
                                                            alternativeProduct,
                                                            activeCategory.alternativeProducts,
                                                            isNewSignUpJourney
                                                        )

                                                    if (
                                                        acquisitionClickoutEventData
                                                    ) {
                                                        logEvents.logAcquisitionJourneyCardsTableClickoutEvent(
                                                            acquisitionClickoutEventData
                                                        )
                                                    }

                                                    onApplyPress({
                                                        alternativeProduct,
                                                        applicationUrl:
                                                            applyUrl,
                                                        productPosition:
                                                            getCardPosition(
                                                                index
                                                            ),
                                                        clickId,
                                                    })
                                                }}
                                            />
                                        </Box>
                                    )
                                }
                            )}
                        </Grid>
                    </Box>
                ) : null}

                <Grid>
                    <Box gridColumn="1/13" mt="space40" mb="space30">
                        <Stack space="space10">
                            <Heading variant="60">
                                Loans alternative products
                            </Heading>
                            <Text variant="120">
                                Check out our partner Little Loans and get a
                                personalised loan offer today.
                            </Text>
                        </Stack>
                    </Box>
                </Grid>

                <Grid>
                    <Box gridColumn="1/13" mb="space30">
                        <LittleLoansOfferCard />
                    </Box>
                </Grid>

                <Grid>
                    <Box gridColumn="1/13" mb="space30">
                        <BrokerInfo />
                    </Box>
                </Grid>
            </Box>
        </>
    )
}

export default AlternativeProductsTable
